import { useTranslation } from 'react-i18next';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router';
import { AppLayout } from '@/components/AppLayout';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Toaster } from '@/components/ui/sonner';
import { SessionProvider, useSession } from '@/lib/session';
import { AccountPage } from '@/pages/AccountPage';
import { AiPage } from '@/pages/AiPage';
import { AuditLogPage } from '@/pages/AuditLogPage';
import { ContactDetailPage, ContactsPage } from '@/pages/ContactsPage';
import { CsatPage } from '@/pages/CsatPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { IntegrationsPage } from '@/pages/IntegrationsPage';
import { LoginPage } from '@/pages/LoginPage';
import { MailPage } from '@/pages/MailPage';
import { MembersPage } from '@/pages/MembersPage';
import { PlatformConnectionsPage } from '@/pages/PlatformConnectionsPage';
import { PlatformMailPage } from '@/pages/PlatformMailPage';
import { PlatformTenantsPage } from '@/pages/PlatformTenantsPage';
import { QueuesPage } from '@/pages/QueuesPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { SnippetsPage } from '@/pages/SnippetsPage';
import { TeamsPage } from '@/pages/TeamsPage';
import { TicketDetailPage } from '@/pages/TicketDetailPage';
import { TicketsPage } from '@/pages/TicketsPage';

/** Schuetzt alles ausser der Login-Seite. Die API prueft ohnehin erneut. */
function RequireSession() {
  const { t } = useTranslation();
  const { user, loading } = useSession();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        {t('common.loading')}
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
}

export function App() {
  return (
    <ErrorBoundary>
      <SessionProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            {/* Oeffentlich (Spec 8.6): der Kunde hat kein Konto. */}
            <Route path="/csat/:token" element={<CsatPage />} />
            <Route element={<RequireSession />}>
              <Route element={<AppLayout />}>
                <Route index element={<DashboardPage />} />
                <Route path="tickets" element={<TicketsPage />} />
                <Route path="tickets/:ticketId" element={<TicketDetailPage />} />
                <Route path="contacts" element={<ContactsPage />} />
                <Route path="contacts/:contactId" element={<ContactDetailPage />} />
                <Route path="snippets" element={<SnippetsPage />} />
                <Route path="ai" element={<AiPage />} />
                <Route path="queues" element={<QueuesPage />} />
                <Route path="mail" element={<MailPage />} />
                <Route path="teams" element={<TeamsPage />} />
                <Route path="members" element={<MembersPage />} />
                <Route path="integrations" element={<IntegrationsPage />} />
                <Route path="audit-log" element={<AuditLogPage />} />
                {/* Betreiber-Ebene (ADR 0014): ohne Mandant, nur fuer
                    Plattform-Admins — die Seiten pruefen es noch einmal. */}
                <Route path="platform/tenants" element={<PlatformTenantsPage />} />
                <Route path="platform/system-mail" element={<PlatformMailPage />} />
                <Route path="platform/connections" element={<PlatformConnectionsPage />} />
                <Route path="account" element={<AccountPage />} />
                {/* Bis Phase 6 lag die Sicherheitsseite hier. Alte Lesezeichen
                    und Links sollen nicht ins Leere laufen. */}
                <Route
                  path="security"
                  element={<Navigate to="/account?tab=security" replace />}
                />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </SessionProvider>
    </ErrorBoundary>
  );
}
